import React from "react";
import {
  Live,
  NavItem,
  StatusDot,
  NotificationTray,
  Sidebar as SidebarFrame,
  SidebarAccount,
  SidebarGroup,
  useStore,
  Button,
  IconButton,
} from "@thekrystalship/krystal";
import { SECTIONS, placeLit, placesOf, routeOfNotification } from "../lib/router.js";
import { can } from "../lib/persona.js";
import { mergedInbox } from "../lib/notifications.js";
import { sessionStore } from "../lib/sessionStore.js";
import { coverArtBg } from "../lib/art.js";
import { favoritesStore, serversStore } from "../lib/stores.js";

// The panel's sidebar: the design system's frame, filled with the panel's nav, the favourites under
// Servers and the nodes under Cluster, and the tray and the account (whose menu holds Settings) in the foot.

// A node the panel cannot fully drive: unreachable, or refusing this session.
// Offline, or honouring nothing. The session is the cluster's and its health is reported once,
// app-wide; what varies per node is whether that node is serving it.
function isDegraded(host, refusal) {
  return !host.online || !!(refusal && refusal.accepts === "refusing");
}

// How many favourites the sidebar draws. A shortcut list earns its place by being scannable without
// being read; past a handful it is a second servers page, and on a short viewport it would push the
// account and Settings off the bottom. The rest are one click away on Servers, which pins the same
// favourites above its grid.
const FAVORITES_SHOWN = 5;

// A favourite the roster does not hold. Its NODE is the whole question: a node that has not answered
// leaves the shortcut standing with its state unknown, while a node that answered and has no such
// server means the favourite is stale. Drawing either as "offline" would be this surface inventing a
// run-state, so neither gets a coloured dot — the hollow one says there is no reading, which is true
// in both cases and is why the two differ in their wording rather than in their dot.
function missingFavorite(hostId, hosts) {
  if (!hostId) return { stale: false, why: "This favourite records no node, so its state can't be read." };
  const host = hosts.find((h) => h.id === hostId);
  if (!host) return { stale: false, why: "The node this was favourited on isn't connected, so its state can't be read." };
  if (!host.online) return { stale: false, why: host.name + " hasn't answered, so this server's state is unknown." };
  return { stale: true, why: "No server by this id on " + host.name + " any more." };
}

function ServerListItem({ server, active, unknown, tip, onClick, onForget }) {
  const dotColor = {
    online: "var(--success)",
    updating: "var(--warning)",
    // Still up, on its way down — the in-transition tone, not the offline grey.
    stopping: "var(--warning)",
    restarting: "var(--warning)",
    starting: "var(--warning)",
    // Occupied with its own data — the same in-transition tone; the run-state
    // underneath may be either.
    "backing-up": "var(--warning)",
    restoring: "var(--warning)",
    moving: "var(--warning)",
    transferring: "var(--warning)",
    offline: "var(--fg-4)",
    crashed: "var(--danger)",
    // Unreachable rather than down — the same distinction the hero pill draws.
    "library-offline": "var(--warning)",
  }[server.status] || "var(--fg-4)";
  return (
    <div
      className={"server-row" + (active ? " server-row--active" : "") + (unknown ? " server-row--unknown" : "")}
      onClick={onClick}
      data-tip={tip || server.name}
      title={tip || undefined}>
      {/* The initial stands in when there is no cover. Collapsed, the thumbnail is the ONLY identity a
          row has — the name is gone and the gradient placeholder is the same for every server, so two
          art-less favourites would be one square twice. */}
      <div className="server-row__icon" style={{ backgroundImage: coverArtBg(server.cover), backgroundSize: "cover", backgroundPosition: "center" }}>
        {!server.cover && <span className="server-row__initial">{(server.name || server.id || "?").trim().charAt(0).toUpperCase()}</span>}
      </div>
      <span className="server-row__name">{server.name}</span>
      {onForget && (
        <IconButton variant="ghost" size="xs" icon="x" className="server-row__forget"
          onClick={(e) => { e.stopPropagation(); onForget(); }}
          label={"Remove " + server.name + " from favourites"} title="Remove from favourites" />
      )}
      {/* Hollow when there is no reading. A coloured dot here would be a run-state nobody measured. */}
      <span
        className={"server-row__dot" + (unknown ? " server-row__dot--unknown" : "")}
        style={unknown ? undefined : {
          background: dotColor
        }}></span>
    </div>
  );
}

/// The favourites, under Servers. Renders NOTHING when there are none — no header, no placeholder,
/// no invitation. That is what lets it hold permanent chrome: the space it takes is always space
/// somebody asked for, unlike a nav entry that is present whether or not it has anything to say.
///
/// The order is the order they were starred in and nothing re-sorts it. This is a dock, not a feed:
/// a shortcut that moves because a server crashed has stopped being a shortcut, and the dot is what
/// carries the state.
function SidebarFavorites({ ids, hostById, servers, hosts, activeId, onOpen, onViewAll, online }) {
  if (!ids.length) return null;
  const shown = ids.slice(0, FAVORITES_SHOWN);
  const overflow = ids.length - shown.length;
  return (
    // A favourite opens a server's page, which only a node can draw.
    <Live on={online} className="sidebar__favs">
      {shown.map((id) => {
        const server = servers.find((s) => s.id === id);
        if (server) {
          return (
            <ServerListItem
              key={id}
              server={server}
              active={activeId === id}
              onClick={() => onOpen(id)} />
          );
        }
        // Never dropped. A favourite that disappears while a node is rebooting looks exactly like one
        // somebody deleted, and the person cannot tell which happened.
        const { stale, why } = missingFavorite(hostById[id], hosts);
        return (
          <ServerListItem
            key={id}
            server={{ id, name: id, cover: null, status: "unknown" }}
            active={activeId === id}
            unknown
            tip={id + " — " + why}
            onClick={() => onOpen(id)}
            onForget={stale ? () => favoritesStore.forget(id) : null} />
        );
      })}
      {overflow > 0 && (
        <Button variant="ghost" size="sm" icon="ellipsis" className="sidebar__favs-more" onClick={onViewAll}
          data-tip={overflow + " more favourite" + (overflow === 1 ? "" : "s")}>
          <span className="sidebar__favs-more__label">{overflow} more</span>
        </Button>
      )}
    </Live>
  );
}

/// The nodes, under Cluster — the same shortcut idiom the favourites hold under Servers, and the
/// one place the cluster's members are individually reachable from the chrome. Every node is drawn:
/// a node is not something a person opts into the way a server is starred, and a cluster whose
/// members came and went from this list would be unreadable as a list of what the panel drives.
///
/// The set is the connected roster — the nodes this browser drives. A peer discovered but not
/// connected stays on the Cluster page, where its "discovered, not connected" state can be said out
/// loud; a row here would have nothing to say.
///
/// The order is the roster's and nothing re-sorts it — a node that moves because it went offline
/// has stopped being a shortcut, and the dot is what carries the state.
function SidebarNodes({ hosts, activeHostId, onOpen, online }) {
  const refusals = useStore(sessionStore, s => s.nodes);
  if (!hosts.length) return null;
  return (
    <div className="sidebar__nodes">
      {hosts.map((h) => {
        const degraded = isDegraded(h, refusals[h.id]);
        // Three readings, all measured: answering, answering but not fully drivable, silent.
        const state = !h.online ? "offline" : degraded ? "warn" : "ok";
        const why = !h.online ? "hasn't answered"
          : degraded ? "needs a sign-in"
          : "online";
        const name = h.name || h.hostname || h.id;
        return (
          <NavItem key={h.id} compact icon="server" label={name} active={activeHostId === h.id} disabled={!online}
            aside={<StatusDot tone={state === "ok" ? "ok" : state === "warn" ? "warn" : "neutral"} label={why} />}
            tip={name + " — " + why} title={name + " — " + why} aria-label={name + " — " + why}
            onClick={() => onOpen(h.id)} />
        );
      })}
    </div>
  );
}

// The signed-in user, pinned at the bottom of the foot: the design system's account row and menu.
// Profile and Settings are the panel's own pages; Account is the cluster's sign-in provider's, which
// opens in a new tab.
function Account({ user, onProfile, onSettings, onLogout, collapsed }) {
  return (
    <SidebarAccount
      user={user}
      collapsed={collapsed}
      onProfile={onProfile}
      onSettings={onSettings}
      onAccount={() => { const page = sessionStore.accountPage(); if (page) window.open(page, "_blank", "noopener"); }}
      onSignOut={onLogout} />
  );
}

function Sidebar({ route = {}, onNavigate, online = true, serversCount = 0, serversTone = "info", clusterCount = 0, clusterTone = "info", attentionCount = 0, attentionTone = "info", user, onLogout, hosts = [], open, collapsed, onToggleCollapse }) {
  // Routing is the ONE contract from App: `route` (the current route) in, a single
  // `onNavigate({ kind })` out. Active state derives from route.kind here rather
  // than a bespoke boolean per entry plumbed down from App.
  const go = (kind) => () => onNavigate && onNavigate({ kind });
  // Read here rather than threaded from App: favourites are the sidebar's own business, and the one
  // routing contract above stays `route` in, `onNavigate` out.
  const favIds = useStore(favoritesStore, (s) => s.ids);
  const favHostById = useStore(favoritesStore, (s) => s.hostById);
  const servers = useStore(serversStore, (s) => s.list);

  // A count is in the rail's tooltip as well as on the badge, because the collapsed rail shrinks
  // the badge to a pip.
  const counted = (label, n) => label + (n > 0 ? " · " + n : "");
  const counts = {
    servers: { n: serversCount, tone: serversTone },
    alerts: { n: attentionCount, tone: attentionTone },
    cluster: { n: clusterCount, tone: clusterTone },
  };
  // What a place carries under its own row: the favourites are shortcuts INTO the list above them,
  // the way the nodes are into the cluster.
  const activeHostId = route.kind === "cluster" ? route.hostId : null;
  const openHost = (id) => onNavigate && onNavigate({ kind: "cluster", hostId: id });
  const extras = {
    servers: (
      <SidebarFavorites
        ids={favIds}
        hostById={favHostById}
        servers={servers}
        hosts={hosts}
        activeId={route.kind === "server" ? route.id : null}
        onOpen={(id) => onNavigate && onNavigate({ kind: "server", id })}
        onViewAll={go("servers")}
        online={online} />
    ),
    cluster: <SidebarNodes hosts={hosts} activeHostId={activeHostId} onOpen={openHost} online={online} />,
  };
  return (
    <SidebarFrame
      mark="/assets/tks-mark.png"
      wordmark="KGSM"
      open={open}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      foot={<>
        {/* One tray above the account: every connected node's notifications, in time order, each row
            naming its node. A row opens the page of what it was about. */}
        <Live on={online}><NotificationTray inbox={mergedInbox}
          onOpen={(n) => onNavigate && onNavigate(routeOfNotification(n.url))} /></Live>
        {/* Last, because it anchors the foot rather than sitting in the middle of it. Its menu opens
            upward for the same reason, and Profile, Settings and Account are in it. */}
        {user && <Account user={user} onProfile={go("profile")} onSettings={go("settings")} onLogout={onLogout} collapsed={collapsed} />}
      </>}>
      {/* The groups are the router's sections that the sidebar lists, separated by a hairline and
          named by nothing. A label costs a row to say what the icons beside it already say, and the
          collapsed rail hides labels anyway, so both modes read the same way.

          A group the caller holds nothing of is ABSENT, not empty: the hairline is drawn between
          groups, so one left standing would rule off a space with nothing in it. Somebody holding
          no server read, alerts or roster gets one group and no separator, not a ladder. */}
      {SECTIONS.filter((section) => section.sidebar).map((section) => {
        // Nav visibility reads the ONE policy (persona.js), so the breadcrumb and the sidebar can
        // never disagree (kgsm-docs reference/node-api.md me).
        const places = placesOf(section.id).filter((place) => !place.cap || can(place.cap));
        if (!places.length) return null;
        return (
          <SidebarGroup key={section.id}>
            {places.map((place) => {
              const count = counts[place.id];
              return (
                <React.Fragment key={place.id}>
                  <NavItem icon={place.icon} label={place.label} active={placeLit(place, route)} onClick={go(place.kind)}
                    disabled={!online && !place.local}
                    badge={count && count.n} tone={count && count.tone} tip={count && counted(place.label, count.n)} />
                  {extras[place.id]}
                </React.Fragment>
              );
            })}
          </SidebarGroup>
        );
      })}
    </SidebarFrame>
  );
}

export { Sidebar };
